import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Loader2,
    Settings2,
    Image as ImageIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    deleteEmailAccount,
    updateEmailSignature,
} from "../../lib/api";

export default function SettingsDialog({ open, onOpenChange, account, onUpdated, onDeleted }) {
    const [text, setText] = useState("");
    const [imgUrl, setImgUrl] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploadingImg, setUploadingImg] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open && account) {
            setText(account.signature_text || "");
            setImgUrl(account.signature_image_url || "");
        }
    }, [open, account]);

    if (!account) return null;

    const save = async () => {
        setSaving(true);
        try {
            const updated = await updateEmailSignature(account.id, {
                signature_text: text,
                signature_image_url: imgUrl,
            });
            onUpdated(updated);
            toast.success("Signature saved");
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const onPickImage = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        // Convert to data URL — keeps it self-contained (no need for a
        // separate storage upload), small enough for typical signatures.
        if (f.size > 500 * 1024) {
            toast.error("Image too large (max 500 KB for signatures)");
            return;
        }
        setUploadingImg(true);
        const reader = new FileReader();
        reader.onload = () => { setImgUrl(String(reader.result || "")); setUploadingImg(false); };
        reader.onerror = () => { toast.error("Couldn't read image"); setUploadingImg(false); };
        reader.readAsDataURL(f);
    };

    const removeAccount = async () => {
        if (!window.confirm(`Disconnect ${account.email_address}? Stored credentials will be deleted.`)) return;
        try {
            await deleteEmailAccount(account.id);
            toast.success("Account disconnected");
            onDeleted(account.id);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't disconnect");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#0A1128] border border-[#2A3655] text-white max-w-lg max-h-[90vh] overflow-y-auto"
                data-testid="email-settings-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        <Settings2 className="w-5 h-5 text-[#00A3FF]" /> Signature — {account.email_address}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold block mb-1">Text signature</label>
                        <textarea
                            rows={5}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="John Doe&#10;Doe Excavation Co.&#10;(555) 555-1234"
                            data-testid="email-signature-text"
                            className="w-full bg-[#05091A] border border-[#2A3655] rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#00A3FF] resize-none font-mono"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold block mb-1">Image (optional — logo)</label>
                        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} data-testid="email-signature-image-input" />
                        {imgUrl ? (
                            <div className="flex items-start gap-2">
                                <img src={imgUrl} alt="signature" className="max-h-20 border border-[#2A3655] rounded-sm bg-white p-1" />
                                <div className="flex flex-col gap-1">
                                    <button type="button" onClick={() => fileInputRef.current?.click()}
                                        className="text-[10px] uppercase tracking-wider font-bold text-slate-300 hover:text-[#00A3FF] border border-[#2A3655] hover:border-[#00A3FF] px-2 py-1 rounded-sm">
                                        Replace
                                    </button>
                                    <button type="button" onClick={() => setImgUrl("")}
                                        data-testid="email-signature-image-clear"
                                        className="text-[10px] uppercase tracking-wider font-bold text-red-300 hover:text-red-200 border border-red-500/30 hover:bg-red-500/10 px-2 py-1 rounded-sm">
                                        Remove
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingImg}
                                data-testid="email-signature-image-pick"
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm border-2 border-dashed border-[#2A3655] hover:border-[#00A3FF] text-slate-400 hover:text-[#00A3FF] text-xs font-bold uppercase tracking-wider">
                                {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                                Add image
                            </button>
                        )}
                        <p className="text-[10px] text-slate-500 mt-1 italic">PNG/JPG, max 500 KB. Embedded inline.</p>
                    </div>
                    <div className="flex items-center justify-between gap-2 pt-3 border-t border-[#2A3655]">
                        <button
                            type="button"
                            onClick={removeAccount}
                            data-testid="email-account-disconnect"
                            className="px-3 py-2 rounded-sm border border-red-500/30 text-red-300 hover:bg-red-500/10 text-xs font-bold uppercase tracking-wider"
                        >
                            Disconnect account
                        </button>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => onOpenChange(false)} disabled={saving}
                                className="px-4 py-2 rounded-sm border border-[#2A3655] text-slate-300 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
                                Cancel
                            </button>
                            <Button type="button" onClick={save} disabled={saving}
                                data-testid="email-signature-save"
                                className="bg-[#00A3FF] hover:bg-[#3395FF] text-white font-bold h-9 px-4 disabled:opacity-50">
                                {saving ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Saving…</> : "Save"}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
